/**
 * TranscriptPanel
 *
 * Displays a live transcript feed for a treatment check-in call using
 * chat-bubble styling. Operator messages align left (dark slate),
 * provider messages align right (light blue).
 *
 * Supports two modes:
 *   - Standalone (default): Wrapped in a Collapsible card.
 *   - Embedded (`embedded` prop): Renders just the content, intended for
 *     use inside a parent overlay container.
 */
import { Check,ChevronDown, Copy } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  TranscriptSegment,
  useLiveTranscript,
} from '@/hooks/useLiveTranscript';
import { cn } from '@/lib/utils';
import { useGetCheckInTranscriptSegmentsQuery } from '@/services/api/treatmentCheckInV2Api';

import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../ui/collapsible';
import { ScrollArea } from '../../ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';

// ── Helpers ──────────────────────────────────────────────────────────────────

function speakerLabel(speaker: number | null): string {
  if (speaker === 1) return 'Provider';
  // Speaker 0 or null → Operator (matches bubble alignment)
  return 'Operator';
}

// ── Sub-components ───────────────────────────────────────────────────────────

const ChatBubble = ({ segment }: { segment: TranscriptSegment }) => {
  const isOperator = segment.speaker === 0 || segment.speaker === null;

  return (
    <div
      className={cn(
        'flex',
        isOperator ? 'justify-start' : 'justify-end',
      )}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                'max-w-[85%] rounded-xl px-3 py-2 text-xs font-medium leading-relaxed',
                isOperator
                  ? 'rounded-tl-sm bg-slate-800 text-slate-100'
                  : 'rounded-tr-sm bg-sky-100 text-slate-900',
              )}
            >
              {segment.text}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-xs font-medium">
              {speakerLabel(segment.speaker)}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

const CopyButton = ({ segments }: { segments: TranscriptSegment[] }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const text = segments
      .map((s) => `${speakerLabel(s.speaker)}: ${s.text}`)
      .join('\n');
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [segments]);

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7"
      onClick={handleCopy}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-600" />
      ) : (
        <Copy className="h-3.5 w-3.5 text-muted-foreground" />
      )}
    </Button>
  );
};

// ── Component ────────────────────────────────────────────────────────────────

interface TranscriptPanelProps {
  groupKey: string | null;
  /** Matter ID needed to fetch existing segments via REST */
  matterId?: string;
  /** Check-in ID — used to fetch transcript segments via the check-in endpoint */
  checkInId?: string;
  /** Audio conversation ID — when present, existing segments are loaded on mount */
  audioConversationId?: string | null;
  className?: string;
  /** When true, renders without the Collapsible wrapper (for use inside an overlay) */
  embedded?: boolean;
  /** Called when live streaming begins */
  onStreamingStart?: () => void;
  /** Called when the call ends (streaming transitions from true to false) */
  onCallEnded?: () => void;
}

export const TranscriptPanel = ({
  groupKey,
  matterId,
  checkInId,
  audioConversationId,
  className,
  embedded,
  onStreamingStart,
  onCallEnded,
}: TranscriptPanelProps) => {
  const { segments: liveSegments, isStreaming, isConnected } = useLiveTranscript(groupKey);

  // Load existing segments from the REST API when an audio conversation exists
  const { data: existingSegments } = useGetCheckInTranscriptSegmentsQuery(
    { matterId: matterId ?? '', checkInId: checkInId ?? '' },
    { skip: !matterId || !checkInId || !audioConversationId },
  );

  // Merge existing (REST) segments with live (WebSocket) segments, deduplicating
  const segments = useMemo<TranscriptSegment[]>(() => {
    const fromRest: TranscriptSegment[] = (existingSegments ?? []).map((s) => ({
      text: s.text,
      start_time: s.start_time,
      end_time: s.end_time,
      speaker: s.speaker,
    }));

    if (liveSegments.length === 0) return fromRest;
    if (fromRest.length === 0) return liveSegments;

    // Use a simple heuristic: if a live segment's start_time already exists in
    // the REST data, skip it. Otherwise append.
    const existingTimes = new Set(fromRest.map((s) => s.start_time));
    const deduped = liveSegments.filter((s) => !existingTimes.has(s.start_time));
    return [...fromRest, ...deduped];
  }, [existingSegments, liveSegments]);

  const [open, setOpen] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new segments arrive
  useEffect(() => {
    if ((embedded || open) && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [segments.length, open, embedded]);

  // Notify parent when streaming starts
  const streamingNotifiedRef = useRef(false);
  useEffect(() => {
    if (isStreaming && isConnected && !streamingNotifiedRef.current) {
      streamingNotifiedRef.current = true;
      onStreamingStart?.();
    }
  }, [isStreaming, isConnected, onStreamingStart]);

  // Notify parent when call ends (streaming transitions from true to false)
  const prevStreamingRef = useRef(false);
  useEffect(() => {
    if (prevStreamingRef.current && !isStreaming && segments.length > 0) {
      onCallEnded?.();
    }
    prevStreamingRef.current = isStreaming;
  }, [isStreaming, segments.length, onCallEnded]);

  const hasSegments = segments.length > 0;

  // ── Shared transcript content ──────────────────────────────────────────

  const transcriptContent = (
    <ScrollArea className="h-64 px-4 pb-3">
      {!hasSegments ? (
        <p className="py-6 text-center text-sm font-medium text-muted-foreground">
          Transcript will appear when the call starts
        </p>
      ) : (
        <div className="space-y-1.5">
          {segments.map((segment, idx) => (
            <ChatBubble key={idx} segment={segment} />
          ))}
          <div ref={bottomRef} />
        </div>
      )}
    </ScrollArea>
  );

  // ── Status + copy bar ──────────────────────────────────────────────────

  const statusBar = (
    <div className="flex items-center gap-2">
      {isStreaming && isConnected && (
        <Badge
          variant="destructive"
          className="flex items-center gap-1 text-[10px]"
        >
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-white" />
          Live
        </Badge>
      )}
      {!isStreaming && hasSegments && (
        <Badge variant="secondary" className="text-[10px]">
          Complete
        </Badge>
      )}
    </div>
  );

  // ── Embedded mode: no wrapper ──────────────────────────────────────────

  if (embedded) {
    return (
      <div className={cn('flex flex-col', className)}>
        {(hasSegments || (isStreaming && isConnected)) && (
          <div className="flex items-center justify-between px-4 py-2">
            {statusBar}
            {hasSegments && <CopyButton segments={segments} />}
          </div>
        )}
        {transcriptContent}
      </div>
    );
  }

  // ── Standalone mode: Collapsible card ──────────────────────────────────

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn(
        'flex flex-col rounded-xl border bg-white shadow-sm dark:bg-zinc-900',
        className,
      )}
    >
      {/* Header */}
      <div className="flex w-full items-center justify-between px-4 py-3">
        <CollapsibleTrigger className="flex items-center gap-2">
          <span className="text-sm font-semibold">Transcript</span>
          {statusBar}
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform',
              open && 'rotate-180',
            )}
          />
        </CollapsibleTrigger>

        {hasSegments && <CopyButton segments={segments} />}
      </div>

      {/* Content */}
      <CollapsibleContent>
        {transcriptContent}
      </CollapsibleContent>
    </Collapsible>
  );
};
